import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { User, Building2, CreditCard, Mail, Sparkles, Trash2, AlertTriangle, Lock, Eye, EyeOff, BarChart3, ChevronDown, CheckCircle, UsersRound, Code2, BellRing } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { cn } from "@/lib/utils";
import { useWorkspace } from "@/hooks/useWorkspace";
import { useUserRole } from "@/hooks/useUserRole";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { useWorkspaceUsage } from "@/hooks/useWorkspaceUsage";
import { useMemberPermissions } from "@/hooks/useMemberPermissions";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { AUTH_EMAIL_CHANGE_PENDING_DESCRIPTION, getAuthErrorMessage } from "@/lib/auth-messages";
import { STRIPE_PLANS, type PlanType } from "@/lib/stripe-plans";
import { useNavigate } from "react-router-dom";
import { WorkspaceMembersManager } from "@/components/settings/WorkspaceMembersManager";
import { ApiKeysManager } from "@/components/settings/ApiKeysManager";
import { PushNotificationsSettings } from "@/components/settings/PushNotificationsSettings";
import { PlanUsageSection } from "@/components/settings/PlanUsageSection";
import { WorkspaceConversationsExport } from "@/components/settings/WorkspaceConversationsExport";
import { hasPartnerApiSectionAccess } from "@/lib/partner-api";

type WorkspacePlanKey = PlanType | "enterprise";

function normalizeWorkspacePlanKey(plan: string | null | undefined): WorkspacePlanKey {
  if (plan === "intermediario" || plan === "pro" || plan === "enterprise") return plan;
  return "inicial";
}

/** Benefícios por tier (preços ficam no Stripe / página de planos). */
const PLAN_FEATURES: Record<PlanType, readonly string[]> = {
  inicial: [
    "1 agente de IA",
    "3.000 leads",
    "2.000 mensagens/mês",
    "1 instância WhatsApp",
    "1 conta Instagram",
  ],
  intermediario: [
    "3 agentes de IA",
    "20.000 leads",
    "10.000 mensagens/mês",
    "2 instâncias WhatsApp",
    "2 contas Instagram",
  ],
  pro: [
    "8 agentes de IA",
    "100.000 leads",
    "40.000 mensagens/mês",
    "3 instâncias WhatsApp",
    "3 contas Instagram",
  ],
};

const ENTERPRISE_FEATURES = [
  "Recursos e limites conforme contrato",
  "Suporte dedicado",
] as const;

function getWorkspacePlanInfo(plan: string | null | undefined) {
  const key = normalizeWorkspacePlanKey(plan);
  if (key === "enterprise") {
    return { displayName: "Enterprise", features: [...ENTERPRISE_FEATURES] };
  }
  return {
    displayName: STRIPE_PLANS[key].name,
    features: [...PLAN_FEATURES[key]],
  };
}

export default function Settings() {
  const { user, updatePassword, updateEmail } = useAuth();
  const { workspaceId, workspaceName } = useWorkspace(user?.id);
  const { isAdmin, isOwner } = useUserRole();
  const { isOwner: isMemberOwner, isAdmin: isWorkspaceGestor, canAccessBilling } =
    useMemberPermissions();
  const { subscribed, shouldShowPaywall, isWorkspaceMember, isAdmin: isInternalAdmin } = useSubscription();
  const hasApiSectionAccess = hasPartnerApiSectionAccess({
    isInternalAdmin,
    email: user?.email,
    workspaceId,
  });
  const { usageData, loading: usageLoading } = useWorkspaceUsage(workspaceId);
  const [currentPlan, setCurrentPlan] = useState<string>("inicial");
  const [loading, setLoading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [updatingPassword, setUpdatingPassword] = useState(false);
  const [passwordSectionOpen, setPasswordSectionOpen] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [updatingEmail, setUpdatingEmail] = useState(false);
  const [emailSectionOpen, setEmailSectionOpen] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  // Verificar provedores de autenticação
  const hasEmailProvider = user?.identities?.some(id => id.provider === 'email');
  const hasGoogleProvider = user?.identities?.some(id => id.provider === 'google');

  useEffect(() => {
    const fetchWorkspacePlan = async () => {
      if (!workspaceId) return;
      
      const { data, error } = await supabase
        .from("workspaces")
        .select("plan")
        .eq("id", workspaceId)
        .single();

      if (data && !error) {
        setCurrentPlan(data.plan || "inicial");
      }
    };

    fetchWorkspacePlan();
  }, [workspaceId]);

  const handleUpdateEmail = async () => {
    if (!newEmail.trim()) return;
    setUpdatingEmail(true);
    try {
      const { error } = await updateEmail(newEmail.trim());
      if (error) throw error;
      toast({
        title: "Verifique seu email",
        description: AUTH_EMAIL_CHANGE_PENDING_DESCRIPTION,
      });
      setNewEmail("");
      setEmailSectionOpen(false);
    } catch (err: unknown) {
      toast({
        title: "Não foi possível alterar o email",
        description: getAuthErrorMessage(err),
        variant: "destructive",
      });
    } finally {
      setUpdatingEmail(false);
    }
  };

  const handleManageSubscription = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("customer-portal");

      if (error) throw error;

      if (data?.url) {
        // Tentar abrir em nova aba, com fallback para mesma janela se bloqueado
        const newWindow = window.open(data.url, "_blank");
        if (!newWindow) {
          toast({
            title: "Redirecionando...",
            description: "Abrindo portal de gerenciamento.",
          });
          // Fallback: redirecionar na mesma janela
          window.location.href = data.url;
        }
      }
    } catch (error) {
      console.error("Error opening customer portal:", error);
      toast({
        title: "Erro",
        description: "Não foi possível abrir o portal de gerenciamento.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validar senha atual (obrigatório)
    if (!currentPassword) {
      toast({
        title: "Senha atual obrigatória",
        description: "Digite sua senha atual para confirmar a alteração.",
        variant: "destructive",
      });
      return;
    }

    if (newPassword.length < 6) {
      toast({
        title: "Senha muito curta",
        description: "A senha deve ter no mínimo 6 caracteres.",
        variant: "destructive",
      });
      return;
    }

    if (newPassword !== confirmPassword) {
      toast({
        title: "Senhas não coincidem",
        description: "As senhas digitadas não são iguais.",
        variant: "destructive",
      });
      return;
    }

    setUpdatingPassword(true);

    try {
      // Verificar senha atual fazendo re-autenticação
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: user?.email || "",
        password: currentPassword,
      });

      if (signInError) {
        toast({
          title: "Senha atual incorreta",
          description: "A senha atual digitada está incorreta.",
          variant: "destructive",
        });
        setUpdatingPassword(false);
        return;
      }

      // Atualizar para nova senha
      const { error } = await updatePassword(newPassword);

      if (error) {
        toast({
          title: "Erro ao atualizar senha",
          description: error.message,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Senha atualizada!",
          description: "Sua senha foi alterada com sucesso.",
        });
        // Limpar campos e fechar dropdown
        setCurrentPassword("");
        setNewPassword("");
        setConfirmPassword("");
        setPasswordSectionOpen(false);
      }
    } catch (error) {
      toast({
        title: "Erro",
        description: "Ocorreu um erro ao atualizar a senha.",
        variant: "destructive",
      });
    } finally {
      setUpdatingPassword(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (confirmText !== "EXCLUIR") {
      toast({
        title: "Confirmação incorreta",
        description: "Digite EXCLUIR para confirmar a exclusão da conta.",
        variant: "destructive",
      });
      return;
    }

    setDeleting(true);
    try {
      const { error } = await supabase.functions.invoke("delete-account");

      if (error) throw error;

      toast({
        title: "Conta excluída",
        description: "Sua conta foi excluída com sucesso.",
      });

      // Fazer logout local e redirecionar mesmo se a sessão remota já estiver expirada
      await supabase.auth.signOut({ scope: "local" });
      navigate("/");
    } catch (error) {
      console.error("Error deleting account:", error);
      toast({
        title: "Erro",
        description: error instanceof Error ? error.message : "Não foi possível excluir a conta.",
        variant: "destructive",
      });
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
      setConfirmText("");
    }
  };

  const planInfo = getWorkspacePlanInfo(currentPlan);

  return (
    <div className="p-2 sm:p-4 lg:p-6 max-w-5xl mx-auto space-y-4 sm:space-y-5 lg:space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold">Configurações</h1>
        <p className="text-muted-foreground mt-1 sm:mt-2 text-xs sm:text-sm lg:text-base">Gerencie suas preferências e configurações do sistema</p>
      </div>

      <Card>
        <CardContent className="p-3 sm:p-4 lg:p-6">
          <Accordion type="multiple" defaultValue={[]} className="w-full">
            {/* Equipe - Proprietário ou gestor (admin) com workspace ativo */}
            {isWorkspaceGestor && workspaceId && !shouldShowPaywall && (
              <AccordionItem value="team" className="border-b">
                <AccordionTrigger className="hover:no-underline py-3 sm:py-4 lg:py-6 px-2 sm:px-4">
                  <div className="flex items-center gap-2 sm:gap-3 lg:gap-4">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <UsersRound className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
                    </div>
                    <div className="text-left">
                      <h3 className="font-semibold text-sm sm:text-base">Equipe</h3>
                      <p className="text-xs sm:text-sm text-muted-foreground">Gerencie os membros do workspace</p>
                    </div>
                  </div>
                </AccordionTrigger>

                <AccordionContent className="pb-4 sm:pb-5 lg:pb-6 pt-2 px-2 sm:px-4">
                  <div className="pl-0 sm:pl-12 lg:pl-16">
                    <WorkspaceMembersManager workspaceId={workspaceId} />
                  </div>
                </AccordionContent>
              </AccordionItem>
            )}
            
            {/* Uso - Apenas com workspace ativo */}
            {workspaceId && !shouldShowPaywall && (
            <AccordionItem value="uso" className="border-b">
              <AccordionTrigger className="hover:no-underline py-3 sm:py-4 lg:py-6 px-2 sm:px-4">
                <div className="flex items-center gap-2 sm:gap-3 lg:gap-4">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <BarChart3 className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
                  </div>
                  <div className="text-left">
                    <h3 className="font-semibold text-sm sm:text-base">Uso do Plano</h3>
                    <p className="text-xs sm:text-sm text-muted-foreground">Consumo mensal de recursos</p>
                  </div>
                </div>
              </AccordionTrigger>

              <AccordionContent className="pb-4 sm:pb-5 lg:pb-6 pt-2 px-2 sm:px-4">
                <div className="space-y-3 sm:space-y-4 pl-0 sm:pl-12 lg:pl-16">
                  {usageLoading ? (
                    <div className="p-4 bg-muted/50 rounded-lg">
                      <p className="text-sm text-muted-foreground text-center">Carregando uso...</p>
                    </div>
                  ) : usageData ? (
                    <PlanUsageSection usageData={usageData} planDisplayName={planInfo.displayName} />
                  ) : (
                    <div className="p-4 bg-muted/50 rounded-lg">
                      <p className="text-sm text-muted-foreground text-center">Não foi possível carregar os dados de uso.</p>
                    </div>
                  )}
                </div>
              </AccordionContent>
            </AccordionItem>
            )}

            {/* Perfil */}
            <AccordionItem value="perfil" className="border-b">
              <AccordionTrigger className="hover:no-underline py-3 sm:py-4 lg:py-6 px-2 sm:px-4">
                <div className="flex items-center gap-2 sm:gap-3 lg:gap-4">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <User className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
                  </div>
                  <div className="text-left">
                    <h3 className="font-semibold text-sm sm:text-base">Perfil</h3>
                    <p className="text-xs sm:text-sm text-muted-foreground">Informações da sua conta</p>
                  </div>
                </div>
              </AccordionTrigger>

              <AccordionContent className="pb-4 sm:pb-5 lg:pb-6 pt-2 px-2 sm:px-4">
                <div className="space-y-3 sm:space-y-4 pl-0 sm:pl-12 lg:pl-16">
                  <div className="p-4 bg-muted/50 rounded-lg space-y-4">
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Mail className="w-4 h-4 text-primary" />
                        <Label className="text-xs font-medium text-muted-foreground">Email</Label>
                      </div>
                      <Input value={user?.email || ""} disabled className="bg-background" />
                    </div>

                    <Collapsible open={emailSectionOpen} onOpenChange={setEmailSectionOpen}>
                      <CollapsibleTrigger asChild>
                        <Button variant="outline" size="sm" className="w-full justify-between">
                          Alterar Email
                          <ChevronDown className={cn("w-4 h-4 transition-transform", emailSectionOpen && "rotate-180")} />
                        </Button>
                      </CollapsibleTrigger>
                      <CollapsibleContent className="pt-3 space-y-3">
                        <p className="text-xs text-muted-foreground">
                          Um email de confirmação será enviado para o novo endereço antes de efetivar a alteração.
                        </p>
                        <div className="space-y-2">
                          <Label className="text-xs font-medium text-muted-foreground">Novo Email</Label>
                          <Input
                            type="email"
                            placeholder="novo@email.com"
                            value={newEmail}
                            onChange={(e) => setNewEmail(e.target.value)}
                            disabled={updatingEmail}
                          />
                        </div>
                        <Button
                          size="sm"
                          disabled={updatingEmail || !newEmail.trim() || newEmail === user?.email}
                          onClick={handleUpdateEmail}
                        >
                          {updatingEmail ? "Enviando..." : "Confirmar alteração"}
                        </Button>
                      </CollapsibleContent>
                    </Collapsible>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* Workspace - Apenas com workspace ativo */}
            {workspaceId && !shouldShowPaywall && (
            <AccordionItem value="workspace" className="border-b">
              <AccordionTrigger className="hover:no-underline py-3 sm:py-4 lg:py-6 px-2 sm:px-4">
                <div className="flex items-center gap-2 sm:gap-3 lg:gap-4">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Building2 className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
                  </div>
                  <div className="text-left">
                    <h3 className="font-semibold text-sm sm:text-base">Workspace</h3>
                    <p className="text-xs sm:text-sm text-muted-foreground">Configurações do seu espaço de trabalho</p>
                  </div>
                </div>
              </AccordionTrigger>

              <AccordionContent className="pb-4 sm:pb-5 lg:pb-6 pt-2 px-2 sm:px-4">
                <div className="space-y-3 sm:space-y-4 pl-0 sm:pl-12 lg:pl-16">
                  <div className="p-4 bg-muted/50 rounded-lg space-y-4">
                    <div className="space-y-2">
                      <Label className="text-xs font-medium text-muted-foreground">Nome do Workspace</Label>
                      <Input value={workspaceName} disabled className="bg-background" />
                    </div>
                    <WorkspaceConversationsExport
                      workspaceId={workspaceId}
                      workspaceName={workspaceName}
                    />
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>
            )}

            {/* Notificações - Apenas com workspace ativo */}
            {workspaceId && !shouldShowPaywall && user?.id && (
            <AccordionItem value="notifications" className="border-b">
              <AccordionTrigger className="hover:no-underline py-3 sm:py-4 lg:py-6 px-2 sm:px-4">
                <div className="flex items-center gap-2 sm:gap-3 lg:gap-4">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <BellRing className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
                  </div>
                  <div className="text-left">
                    <h3 className="font-semibold text-sm sm:text-base">Notificações</h3>
                    <p className="text-xs sm:text-sm text-muted-foreground">Gerencie alertas do navegador</p>
                  </div>
                </div>
              </AccordionTrigger>

              <AccordionContent className="pb-4 sm:pb-5 lg:pb-6 pt-2 px-2 sm:px-4">
                <div className="pl-0 sm:pl-12 lg:pl-16">
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <PushNotificationsSettings workspaceId={workspaceId} userId={user.id} />
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>
            )}

            {/* Plano - Ocultar completamente para membros de workspace */}
            {!isWorkspaceMember && (
            <AccordionItem value="plano" className="border-b last:border-0">
              <AccordionTrigger className="hover:no-underline py-3 sm:py-4 lg:py-6 px-2 sm:px-4">
                <div className="flex items-center justify-between w-full pr-2 sm:pr-4">
                  <div className="flex items-center gap-2 sm:gap-3 lg:gap-4 min-w-0 flex-1">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <CreditCard className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
                    </div>
                    <div className="text-left min-w-0 flex-1">
                      <h3 className="font-semibold text-sm sm:text-base">Plano e Assinatura</h3>
                      <p className="text-xs sm:text-sm text-muted-foreground">Gerencie seu plano atual</p>
                    </div>
                  </div>
                  <Badge className="bg-primary/10 text-primary hover:bg-primary/20 text-[10px] sm:text-xs flex-shrink-0 ml-2">
                    {!subscribed && !isAdmin ? "Sem assinatura" : planInfo.displayName}
                  </Badge>
                </div>
              </AccordionTrigger>

              <AccordionContent className="pb-4 sm:pb-5 lg:pb-6 pt-2 px-2 sm:px-4">
                <div className="space-y-3 sm:space-y-4 pl-0 sm:pl-12 lg:pl-16">
                  {!subscribed && !isAdmin ? (
                    <div className="p-3 sm:p-4 bg-muted/50 rounded-lg space-y-4">
                      <div className="space-y-1.5">
                        <p className="font-semibold text-sm sm:text-base">Você não tem assinatura ativa</p>
                        <p className="text-xs sm:text-sm text-muted-foreground">
                          Contrate um plano para usar todos os recursos.
                        </p>
                      </div>
                      <Button
                        variant="default"
                        size="sm"
                        className="w-full h-9 sm:h-10"
                        onClick={() => navigate("/planos")}
                      >
                        <Sparkles className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1.5 sm:mr-2" />
                        <span className="text-xs sm:text-sm">Ver planos e contratar</span>
                      </Button>
                    </div>
                  ) : (
                    <>
                      <div className="p-3 sm:p-4 bg-muted/50 rounded-lg">
                        <div className="flex items-start justify-between">
                          <div className="space-y-1.5 sm:space-y-2">
                            <div className="flex items-center gap-1.5 sm:gap-2">
                              <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
                              <p className="font-semibold text-sm sm:text-base">{planInfo.displayName}</p>
                            </div>
                            <ul className="text-xs sm:text-sm text-muted-foreground space-y-0.5 sm:space-y-1 mt-2 sm:mt-3">
                              {planInfo.features.map((feature, index) => (
                                <li key={index}>• {feature}</li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>

                      {isInternalAdmin ? (
                        <div className="p-3 sm:p-4 bg-primary/10 rounded-lg">
                          <p className="text-xs sm:text-sm text-primary font-medium text-center">
                            Workspace interno — não há portal de assinatura Stripe para este ambiente. Para faturamento comercial, fale com o suporte.
                          </p>
                        </div>
                      ) : (
                        <Button
                          variant="default"
                          size="sm"
                          className="w-full h-9 sm:h-10"
                          onClick={handleManageSubscription}
                          disabled={loading}
                        >
                          <CreditCard className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1.5 sm:mr-2" />
                          <span className="text-xs sm:text-sm">
                            {loading ? "Carregando..." : "Gerenciar assinatura no Stripe"}
                          </span>
                        </Button>
                      )}
                    </>
                  )}
                </div>
              </AccordionContent>
            </AccordionItem>
            )}

            {/* Segurança */}
            <AccordionItem value="security" className="border-b">
              <AccordionTrigger className="hover:no-underline py-3 sm:py-4 lg:py-6 px-2 sm:px-4">
                <div className="flex items-center gap-2 sm:gap-3 lg:gap-4">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Lock className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
                  </div>
                  <div className="text-left">
                    <h3 className="font-semibold text-sm sm:text-base">Segurança</h3>
                    <p className="text-xs sm:text-sm text-muted-foreground">Senha e vinculação de contas</p>
                  </div>
                </div>
              </AccordionTrigger>

              <AccordionContent className="pb-4 sm:pb-5 lg:pb-6 pt-2 px-2 sm:px-4">
                <div className="space-y-3 sm:space-y-4 pl-0 sm:pl-12 lg:pl-16">
                  
                  {/* Seção de Senha - Só aparece se usuário tem provedor email */}
                  {hasEmailProvider && (
                    <Collapsible 
                      open={passwordSectionOpen} 
                      onOpenChange={setPasswordSectionOpen}
                    >
                      <div className="p-4 bg-muted/50 rounded-lg">
                        <CollapsibleTrigger asChild>
                          <Button 
                            variant="ghost" 
                            className="w-full justify-between p-0 h-auto hover:bg-transparent"
                          >
                            <div className="flex items-center gap-2">
                              <Lock className="w-4 h-4" />
                              <span className="font-medium">Alterar Senha</span>
                            </div>
                            <ChevronDown className={cn(
                              "h-4 w-4 transition-transform",
                              passwordSectionOpen && "rotate-180"
                            )} />
                          </Button>
                        </CollapsibleTrigger>
                        
                        <CollapsibleContent className="pt-4">
                          <form onSubmit={handleUpdatePassword} className="space-y-4">
                            {/* Campo: Senha Atual */}
                            <div className="space-y-2">
                              <Label htmlFor="current-password">Senha Atual</Label>
                              <div className="relative">
                                <Input
                                  id="current-password"
                                  type={showCurrentPassword ? "text" : "password"}
                                  placeholder="Digite sua senha atual"
                                  value={currentPassword}
                                  onChange={(e) => setCurrentPassword(e.target.value)}
                                  required
                                  className="pr-10"
                                />
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                                >
                                  {showCurrentPassword ? (
                                    <EyeOff className="h-4 w-4 text-muted-foreground" />
                                  ) : (
                                    <Eye className="h-4 w-4 text-muted-foreground" />
                                  )}
                                </Button>
                              </div>
                            </div>

                            {/* Campo: Nova Senha */}
                            <div className="space-y-2">
                              <Label htmlFor="new-password">Nova Senha</Label>
                              <div className="relative">
                                <Input
                                  id="new-password"
                                  type={showNewPassword ? "text" : "password"}
                                  placeholder="••••••••"
                                  value={newPassword}
                                  onChange={(e) => setNewPassword(e.target.value)}
                                  required
                                  minLength={6}
                                  className="pr-10"
                                />
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                                  onClick={() => setShowNewPassword(!showNewPassword)}
                                >
                                  {showNewPassword ? (
                                    <EyeOff className="h-4 w-4 text-muted-foreground" />
                                  ) : (
                                    <Eye className="h-4 w-4 text-muted-foreground" />
                                  )}
                                </Button>
                              </div>
                              <p className="text-xs text-muted-foreground">Mínimo 6 caracteres</p>
                            </div>

                            {/* Campo: Confirmar Nova Senha */}
                            <div className="space-y-2">
                              <Label htmlFor="confirm-password">Confirmar Nova Senha</Label>
                              <div className="relative">
                                <Input
                                  id="confirm-password"
                                  type={showConfirmPassword ? "text" : "password"}
                                  placeholder="••••••••"
                                  value={confirmPassword}
                                  onChange={(e) => setConfirmPassword(e.target.value)}
                                  required
                                  minLength={6}
                                  className="pr-10"
                                />
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                >
                                  {showConfirmPassword ? (
                                    <EyeOff className="h-4 w-4 text-muted-foreground" />
                                  ) : (
                                    <Eye className="h-4 w-4 text-muted-foreground" />
                                  )}
                                </Button>
                              </div>
                            </div>

                            {/* Botão de Confirmar */}
                            <Button 
                              type="submit" 
                              className="w-full"
                              disabled={updatingPassword}
                            >
                              <Lock className="w-4 h-4 mr-2" />
                              {updatingPassword ? "Atualizando..." : "Confirmar Alteração"}
                            </Button>
                          </form>
                        </CollapsibleContent>
                      </div>
                    </Collapsible>
                  )}

                  {/* Mensagem para usuários só-Google */}
                  {!hasEmailProvider && hasGoogleProvider && (
                    <div className="p-4 bg-muted/50 rounded-lg">
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Lock className="w-4 h-4" />
                        <p className="text-sm">
                          Você fez login via Google. Não é necessário configurar uma senha.
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Seção Google - Status de vinculação */}
                  <div className="p-4 bg-muted/50 rounded-lg space-y-3">
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Conta Google</Label>
                      {hasGoogleProvider ? (
                        <div className="flex items-center gap-2 text-sm text-green-600">
                          <CheckCircle className="w-4 h-4" />
                          <span>Conta Google vinculada</span>
                        </div>
                      ) : (
                        <p className="text-xs text-muted-foreground">
                          Para vincular uma conta Google, faça login com o Google na próxima vez.
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* API - Apenas para owners/admins com workspace ativo */}
            {hasApiSectionAccess && isMemberOwner && workspaceId && !shouldShowPaywall && user?.id && (
              <AccordionItem value="api" className="border-b">
                <AccordionTrigger className="hover:no-underline py-3 sm:py-4 lg:py-6 px-2 sm:px-4">
                  <div className="flex items-center gap-2 sm:gap-3 lg:gap-4">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Code2 className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
                    </div>
                    <div className="text-left">
                      <h3 className="font-semibold text-sm sm:text-base">API</h3>
                      <p className="text-xs sm:text-sm text-muted-foreground">Chaves, webhooks e referência interna</p>
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pb-4 sm:pb-5 lg:pb-6 pt-2 px-2 sm:px-4">
                  <div className="pl-0 sm:pl-12 lg:pl-16">
                    <ApiKeysManager workspaceId={workspaceId} userId={user.id} />
                  </div>
                </AccordionContent>
              </AccordionItem>
            )}

            {/* Zona de Perigo */}
            <AccordionItem value="danger" className="border-b-0">
              <AccordionTrigger className="hover:no-underline py-3 sm:py-4 lg:py-6 px-2 sm:px-4">
                  <div className="flex items-center gap-2 sm:gap-3 lg:gap-4">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center flex-shrink-0">
                      <AlertTriangle className="w-5 h-5 sm:w-6 sm:h-6 text-amber-600 dark:text-amber-500" />
                    </div>
                    <div className="text-left">
                      <h3 className="font-semibold text-sm sm:text-base">Zona de Perigo</h3>
                      <p className="text-xs sm:text-sm text-muted-foreground">Ações irreversíveis</p>
                    </div>
                  </div>
                </AccordionTrigger>

                <AccordionContent className="pb-4 sm:pb-5 lg:pb-6 pt-2 px-2 sm:px-4">
                  <div className="space-y-3 sm:space-y-4 pl-0 sm:pl-12 lg:pl-16">
                    <div className="p-5 border border-amber-200 dark:border-amber-900/50 rounded-xl bg-amber-50/50 dark:bg-amber-950/20 space-y-4">
                      <div className="space-y-2">
                        <h4 className="font-semibold text-sm flex items-center gap-2">
                          <Trash2 className="w-4 h-4 text-muted-foreground" />
                          Excluir Conta
                        </h4>
                        <p className="text-sm text-muted-foreground leading-relaxed">
                          Esta ação é permanente. Todos os dados do workspace serão excluídos.
                        </p>
                      </div>
                      <Button 
                        variant="destructive" 
                        onClick={() => setDeleteDialogOpen(true)}
                        className="w-full sm:w-auto"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Excluir Minha Conta
                      </Button>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
          </Accordion>
        </CardContent>
      </Card>

      {/* Dialog de Confirmação de Exclusão */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader className="space-y-4">
            <AlertDialogTitle className="flex items-center gap-3 text-lg font-semibold">
              <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-500" />
              </div>
              Excluir Conta
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Esta ação irá remover permanentemente:
                </p>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <span className="text-muted-foreground/70">•</span>
                    Todos os agentes e suas configurações
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-muted-foreground/70">•</span>
                    Todos os leads e conversas
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-muted-foreground/70">•</span>
                    Todas as integrações e produtos
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-muted-foreground/70">•</span>
                    Todo o workspace e seus dados
                  </li>
                </ul>
                
                {subscribed && (
                  <div className="p-4 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900/50 rounded-lg">
                    <p className="text-amber-700 dark:text-amber-400 text-sm font-medium flex items-center gap-2 mb-2">
                      <CreditCard className="w-4 h-4" />
                      Aviso sobre sua assinatura
                    </p>
                    <ul className="space-y-1.5 text-xs text-amber-600/90 dark:text-amber-400/80">
                      <li className="flex items-start gap-2">
                        <span className="text-amber-500">•</span>
                        Assinatura cancelada automaticamente
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-amber-500">•</span>
                        Sem reembolso do período atual
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-amber-500">•</span>
                        Perda de acesso imediata
                      </li>
                    </ul>
                  </div>
                )}
                
                <div className="pt-2 space-y-2">
                  <Label htmlFor="confirm-text" className="text-sm text-muted-foreground">
                    Digite <span className="font-semibold text-foreground">EXCLUIR</span> para confirmar
                  </Label>
                  <Input
                    id="confirm-text"
                    value={confirmText}
                    onChange={(e) => setConfirmText(e.target.value)}
                    placeholder="EXCLUIR"
                    className="font-mono text-center"
                  />
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2 sm:gap-0">
            <AlertDialogCancel onClick={() => setConfirmText("")} className="border-0 bg-transparent hover:bg-muted">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteAccount}
              disabled={confirmText !== "EXCLUIR" || deleting}
              className="bg-destructive hover:bg-destructive/90"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              {deleting ? "Excluindo..." : "Excluir"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
